import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import type { Page, Project } from '../../lib/types';
import {
  HEALTH_LABEL,
  HEALTH_TONE,
  PRIORITY_TONE,
  PROJECT_STATUS_TONE,
  formatDate,
  titleCase,
} from '../../lib/format';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNotice,
  Loading,
  Table,
  Td,
  Th,
  Tr,
} from '../../components/ui';

export function AccountProjectsTab({
  accountId,
}: {
  accountId: string;
  accountName: string;
}) {
  const query = useQuery({
    queryKey: ['projects', accountId, 'all'],
    queryFn: () => api.get<Page<Project>>('/projects', { accountId, limit: 100 }),
  });

  const projects = query.data?.data ?? [];

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorNotice error={query.error} onRetry={() => query.refetch()} />;
  if (projects.length === 0) {
    return (
      <EmptyState
        title="No projects"
        description="Projects group the deliverables and work items that make up a programme of work."
      />
    );
  }

  return (
    <Card padded={false}>
      <Table>
        <thead>
          <tr>
            <Th>Project</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Health</Th>
            <Th>Current focus</Th>
            <Th>Next milestone</Th>
            <Th>Owner</Th>
            <Th align="right">Deliverables</Th>
            <Th align="right">Target</Th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <Tr key={project.id}>
              <Td>
                <Link
                  to={`/projects/${project.id}`}
                  className="font-medium text-accent hover:underline"
                >
                  {project.name}
                </Link>
                <span className="mt-0.5 block">
                  <Badge tone={PRIORITY_TONE[project.priority]} dot={project.priority === 'critical'}>
                    {titleCase(project.priority)}
                  </Badge>
                </span>
              </Td>
              <Td className="text-ink-2">{titleCase(project.projectType)}</Td>
              <Td>
                <Badge tone={PROJECT_STATUS_TONE[project.status]}>{titleCase(project.status)}</Badge>
              </Td>
              <Td>
                <Badge tone={HEALTH_TONE[project.health]}>{HEALTH_LABEL[project.health]}</Badge>
              </Td>
              <Td className="max-w-56 text-xs text-ink-2">
                <span className="line-clamp-2">{project.currentFocus ?? '—'}</span>
              </Td>
              <Td className="max-w-48 text-xs text-ink-2">
                <span className="line-clamp-2">{project.nextMilestoneSummary ?? '—'}</span>
              </Td>
              <Td className="text-xs text-ink-2">{project.ownerName}</Td>
              <Td align="right" className="tnum text-xs text-ink-2">
                {project.completedDeliverableCount ?? 0}/{project.deliverableCount ?? 0}
              </Td>
              <Td align="right" className="text-xs whitespace-nowrap text-muted">
                {formatDate(project.plannedEndDate)}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}
